/**
 * One run submission per query tab at a time, from the first click through
 * the transaction BEGIN (if any) to the dispatch.
 *
 * `isExecuting` flips only once a statement is dispatched, so while a Manual
 * or Smart tab's BEGIN was on the wire Execute stayed enabled: a second click
 * joined the same BEGIN and both statements ran — two UPDATEs on MySQL
 * (Codex r5). Execute (every trigger: toolbar, keyboard, selection and cursor
 * bars, agent requests) and the results-grid Save claim this gate
 * synchronously when they start and hand it back once their statement is
 * dispatched or they give up; a submission that finds it taken is ignored.
 * After dispatch the existing `isExecuting` / `isSaving` interlocks apply.
 */

/** A claim is an identity, so a stale holder can never release a newer claim. */
export type TabSubmissionClaim = symbol;

const submitting = new Map<string, TabSubmissionClaim>();

/** Claim the tab's gate; `null` when another submission holds it (ignore this one). */
export function tryClaimTabSubmission(tabId: string): TabSubmissionClaim | null {
  if (submitting.has(tabId)) return null;
  const claim: TabSubmissionClaim = Symbol(tabId);
  submitting.set(tabId, claim);
  return claim;
}

/** Hand back THIS claim — a no-op if the gate was reset and claimed again since. */
export function releaseTabSubmission(tabId: string, claim: TabSubmissionClaim): void {
  if (submitting.get(tabId) === claim) submitting.delete(tabId);
}

/**
 * A restored tab instance under the same id starts with a free gate: the
 * submission holding it belonged to the previous instance, whose BEGIN has
 * already lost ownership (Codex r6 — it used to reject Execute until that old
 * BEGIN settled, up to its 30 s deadline).
 */
export function resetTabSubmission(tabId: string): void {
  submitting.delete(tabId);
}

export function isTabSubmitting(tabId: string): boolean {
  return submitting.has(tabId);
}
